import type { ReactNode, SVGProps } from "react";
import { cls } from "@op/utils";
import type { HeadingProps } from "@op/puck-types";

type BrandElementProps = SVGProps<SVGSVGElement>;

const BrandElement = ({ className, ...rest }: BrandElementProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="160"
      height="240"
      fill="none"
      viewBox="0 0 240 240"
      className={cls("absolute inset-0 h-full", className)}
      {...rest}
    >
      <path
        fill="currentColor"
        d="M191.594 240L84.721 0h48.407l106.873 240h-48.407zM106.873 240L0 0h48.407L155.28 240h-48.407z"
      ></path>
    </svg>
  );
};

function getComponent(level: "section" | "subsection") {
  switch (level) {
    case "section":
      return "h2";
    case "subsection":
      return "h3";
    default:
      return "span";
  }
}

function getHeadingClass(level: "section" | "subsection") {
  switch (level) {
    case "section":
      return "text-heading-section font-heading-section";
    case "subsection":
      return "text-heading-subsection font-heading-subsection";
    default:
      return undefined;
  }
}

function getSpacing(level: "section" | "subsection") {
  switch (level) {
    case "section":
      return "mt-2";
    case "subsection":
      return "mt-2";
    default:
      return undefined;
  }
}

function getColorClass(level: "section" | "subsection") {
  switch (level) {
    case "section":
      return "text-brand-700";
    case "subsection":
      return "text-default-font";
    default:
      return undefined;
  }
}

const Wrapper = ({
  isH2,
  children,
}: {
  isH2: boolean;
  children: ReactNode;
}) => {
  return isH2 ? (
    <div
      className={cls("relative py-4 uppercase")}
      style={{ breakAfter: "avoid-page" }}
    >
      {children}
    </div>
  ) : (
    <div style={{ breakAfter: "avoid-page" }}>{children}</div>
  );
};

const HeadingWzb = (
  props: HeadingProps & {
    as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div";
  }
) => {
  const { children, level, as, className, slug, ...rest } = props;
  const Component = as ?? getComponent(level);

  return (
    <Wrapper isH2={level === "section"}>
      {level === "section" && (
        <div
          className={cls(
            "absolute z-0 top-0 bottom-0 left-0 w-[250px] text-brand-50 heading-node"
          )}
        >
          <BrandElement className="brand-element" />
        </div>
      )}
      <Component
        {...rest}
        className={cls(
          getSpacing(level),
          getColorClass(level),
          getHeadingClass(level),
          className,
          "relative z-10"
        )}
        id={slug}
      >
        {children}
      </Component>
    </Wrapper>
  );
};

export default HeadingWzb;
